import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import ContentPage from "components/contentPage/ContentPage";
import { FMSThemeContext } from "contexts/FMSThemeContext";
import { BranchFavouriteContext } from "contexts/BranchFavouriteContext";
import { updateBranch } from "api/PostBranch";
import { invalidateBranchesCache } from "hooks/Branches";
import {
  CateringEventTier,
  TIER_DISPLAY_NAMES,
  MANPOWER_CATEGORIES,
} from "types/CateringEvent";
import Toast, { IToastBasicProps } from "components/Toast/Toast";
import styles from "./ManpowerConfig.module.css";

const PAX_BUCKETS = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000];
const TIERS = [CateringEventTier.TIER_1, CateringEventTier.TIER_2, CateringEventTier.TIER_3];

const ManpowerConfig: React.FC = () => {
  const { branchId } = useParams<{ branchId: string }>();
  const history = useHistory();
  const { primaryColor, primaryTextColor } = useContext(FMSThemeContext);
  const { branches } = useContext(BranchFavouriteContext);

  const branch = branches.find((b) => b.id === branchId);

  const [config, setConfig] = useState<Record<string, Record<string, Record<string, number>>>>({});
  const [selectedPax, setSelectedPax] = useState("100");
  const [selectedTier, setSelectedTier] = useState<CateringEventTier>(CateringEventTier.TIER_1);
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showToast, setShowToast] = useState<IToastBasicProps>({
    open: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    if (branch && !loaded) {
      setConfig(branch.manpowerEstimationConfig ?? {});
      setLoaded(true);
    }
  }, [branch, loaded]);

  if (!branch) {
    return (
      <ContentPage
        header={<div className={styles.pageName}>Branch Not Found</div>}
        mainContent={
          <div className={styles.emptyState}>
            Branch not found.{" "}
            <span className={styles.backLink} onClick={() => history.push("/settings")}>
              Back to settings
            </span>
          </div>
        }
      />
    );
  }

  const currentEstimates = config[selectedPax]?.[selectedTier] ?? {};

  const updateCategory = (category: string, value: number) => {
    setConfig((prev) => {
      const next = { ...prev };
      if (!next[selectedPax]) next[selectedPax] = {};
      if (!next[selectedPax][selectedTier]) next[selectedPax][selectedTier] = {};
      next[selectedPax] = { ...next[selectedPax] };
      next[selectedPax][selectedTier] = { ...next[selectedPax][selectedTier], [category]: value };
      return next;
    });
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const error = await updateBranch(branch.id, {
        ...branch,
        manpowerEstimationConfig: config,
      });
      if (error) {
        setShowToast({ open: true, message: error.message, type: "error" });
      } else {
        invalidateBranchesCache();
        setShowToast({ open: true, message: "Manpower config saved", type: "success" });
      }
    } catch {
      setShowToast({ open: true, message: "Failed to save", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContentPage
      header={
        <>
          <div className={styles.pageName}>Manpower Config</div>
          <span className={styles.backLink} onClick={() => history.push("/settings")}>
            &larr; Back to Settings
          </span>
        </>
      }
      mainContent={
        <div className={styles.container}>
          <Toast
            open={showToast.open}
            message={showToast.message}
            type={showToast.type}
            onClose={() => setShowToast({ ...showToast, open: false })}
          />

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{branch.name}</div>
            <div className={styles.selectors}>
              <div className={styles.formField}>
                <label className={styles.label}>PAX Bucket</label>
                <select
                  className={styles.select}
                  value={selectedPax}
                  onChange={(e) => setSelectedPax(e.target.value)}
                >
                  {PAX_BUCKETS.map((p) => (
                    <option key={p} value={String(p)}>
                      {p} PAX
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Tier</label>
                <select
                  className={styles.select}
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value as CateringEventTier)}
                >
                  {TIERS.map((t) => (
                    <option key={t} value={t}>
                      {TIER_DISPLAY_NAMES[t]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {MANPOWER_CATEGORIES.map((cat) => (
              <div key={cat} className={styles.estimateRow}>
                <span className={styles.estimateName}>{cat}</span>
                <input
                  type="number"
                  className={styles.estimateQtyInput}
                  min={0}
                  value={currentEstimates[cat] ?? 0}
                  onChange={(e) => updateCategory(cat, parseInt(e.target.value) || 0)}
                />
              </div>
            ))}
          </div>

          <div className={styles.submitRow}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={submitting}
              style={{ backgroundColor: primaryColor, color: primaryTextColor }}
            >
              {submitting ? "Saving..." : "Save Config"}
            </Button>
            <Button variant="outlined" onClick={() => history.push("/settings")} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default ManpowerConfig;
